import React from 'react';
import { Box, Typography, styled, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { ApprovalState } from '../../../features/NewSolutionXPLR/shared/types/solution-xplr.interface';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface ArrowStepperProps {
  steps: string[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  activeColor?: string;
  height?: string | number;
  showNumber?: boolean;
  disableStepClick?: boolean;
  showStepIcon?: boolean;
  approvalState?: ApprovalState;
  solutionPresent?: boolean;
}

const StepperContainer = styled(Box)({
  display: 'flex',
  width: '100%',
  minWidth: '100%',
  overflowX: 'auto',
  overflowY: 'hidden',
  position: 'relative',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    height: '6px',
    display: 'none'
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '6px',
  },
  msOverflowStyle: 'none', // IE
  // Background gradient line
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: 'calc(100% - 30px)',
    background: (props: any) => {
      const completedPercentage = ((props.activeStep || 0) / (props.totalSteps - 1)) * 100;
      return `linear-gradient(90deg, #317F9F 0%, #317F9F ${completedPercentage}%, lightgrey ${completedPercentage}%, lightgray 100%)`;
    },
    zIndex: 0,
  },
});

const ArrowStep = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isActive', 'isCompleted', 'activeColor', 'isLast', 'isFirst', 'height'].includes(
      prop as string
    ),
})<{
  isActive: boolean;
  isCompleted: boolean;
  activeColor: string;
  isLast: boolean;
  isFirst: boolean;
  height: string | number;
}>(({ isActive, isLast, isFirst, height }) => {
  const space = '30px';
  const borderColor = isActive ? '#1A55F9' : '#B0B0B0';
  // Background colors based on state
  const getBgColor = () => {
    if (isActive) return '#1A55F9';
    return 'white';
  };

  const getBorderColor = () => {
    if (isActive) return '#1A55F9';
    return 'lightgrey';
  };

  // Clip path based on position
  const getClipPath = () => {
    if (isFirst) {
      return `polygon(0% 0%, calc(100% - ${space}) 0%, 100% 50%, calc(100% - ${space}) 100%, 0% 100%)`;
    } else if (isLast) {
      return `polygon(${space} 50%, 0% 0%, calc(100% - ${space}) 0%, 100% 50%, calc(100% - ${space}) 100%, 0% 100%)`;
    } else {
      return `polygon(${space} 50%, 0% 0%, calc(100% - ${space}) 0%, 100% 50%, calc(100% - ${space}) 100%, 0% 100%)`;
    }
  };

  return {
    flex: '100%',
    padding: '10px',
    background: getBgColor(),
    margin: isFirst ? '0' : '0 0 0 calc(calc(-1 * 30px) + 1px)',
    clipPath: getClipPath(),
    WebkitClipPath: getClipPath(),
    borderTop: `1px solid ${getBorderColor()}`,
    borderBottom: `1px solid ${getBorderColor()}`,
    borderLeft: isFirst ? `1px solid ${getBorderColor()}` : 'none',
    //borderRight: isLast ? '1px solid lightgrey' : 'none',
    borderTopLeftRadius: isFirst ? '5px' : '0',
    borderBottomLeftRadius: isFirst ? '5px' : '0',
//     borderRight: 'none',
// borderTopRightRadius: '0',
// borderBottomRightRadius: '0',
    cursor: 'pointer',
    color: isActive ? 'white' : '#000000',
    fontWeight: isActive ? 'bold' : 'normal',
    position: 'relative',
    zIndex: 1,
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '200px',
    transition: 'all 0.3s ease',

    '&:hover': {
      backgroundColor: !isActive ? '#f5f5f5' : undefined,
    },
    // ✅ Pseudo-element se border banao
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: '-1px',  // 1px bahar — border thickness
      background: borderColor,
      clipPath: getClipPath(),
      WebkitClipPath: getClipPath(),
      zIndex: -1,
    },

    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '0px',
      background: isActive ? '#1A55F9' : 'white',
      clipPath: getClipPath(),
      WebkitClipPath: getClipPath(),
      zIndex: -1,
    },
    '@media (max-width: 760px)': {
      flex: '0 0 200px',
    },
  };
});

const StepCircle = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '16px',
  height: '16px',
  borderRadius: '50%',
  border: `2px solid ${isActive ? '#FFFFFF' : '#B0B0B0'}`,
  backgroundColor: 'primary.main',
  color: '#555',
  marginRight: '12px',
  fontSize: '0.9rem',
  fontWeight: 'bold',
  flexShrink: 0,
}));

const ApprovalIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '12px',
  flexShrink: 0,
});

const StepLabel = styled(Typography)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '120%',
  letterSpacing: '-0.01em',
  maxWidth: 'auto',
});

const getColorMap = (text: string) => {
  switch (text.toLowerCase()) {
    case 'approval pending':
      return { bg: '#FFF5F2', color: '#FF6E49' };
    case 'approved':
      return { bg: '#F1FAF1', color: '#23A566' };
    default:
      return { bg: '#FFF5F2', color: '#FF6E49' };
  }
};

const nameMap = {
  'Targeted Process': 'targeted_process',
  'Agentic Workflow': 'agentic_workflow',
  'Benefits': 'benefits_and_impact',
  'Feasibility': 'feasibility_and_impact',
  'Feasibility and Impact': 'feasibility_and_impact',
  'AI Solution Blueprint': 'ai_solution_blueprint',
  'Solution Requirements': 'solution_requirements',
  'Data Sources': 'data_sources',
};

const NewArrowStepper: React.FC<ArrowStepperProps> = ({
  steps,
  activeStep,
  setActiveStep,
  activeColor = '#1A55F9',
  height = '60px',
  showNumber = false,
  disableStepClick = false,
  showStepIcon = false,
  approvalState,
  solutionPresent,
}) => {
  const generalInfo = approvalState
    ? { approval_state: approvalState }
    : useSelector((state: RootState) => state.solutionXPLR);
  const SolutionIsThere = approvalState ? solutionPresent : true;

  const handleStepClick = (index: number) => {
    if (disableStepClick) return;
    setActiveStep(index);
  };

  const isStepApproved = (label: string) => {
    if (!SolutionIsThere) return false;
    if (
      ![
        'Targeted Process',
        'Agentic Workflow',
        'Feasibility and Impact',
        'AI Solution Blueprint',
        'Solution Requirements',
        'Data Sources',
        'Benefits',
        'Feasibility',
      ].includes(label)
    )
      return false;

    const key = nameMap[label as keyof typeof nameMap] || label.toLowerCase().replace(/ /g, '_');
    return generalInfo?.approval_state?.[key] === 'APPROVED';
  };

  const nameMapWithNewFlow = {
    ...nameMap,
    'Benefits': 'benefits_and_impact',
    'Feasibility': 'feasibility_and_impact',
  };

  return (
    <>
      <StepperContainer
        sx={{
          '&::before': {
            background: `linear-gradient(90deg, lightgrey 0%, lightgrey ${(activeStep / (steps.length - 1)) * 100}%, lightgrey ${(activeStep / (steps.length - 1)) * 100}%, lightgray 100%)`,
          },
        }}
      >
        {steps.map((label, index) => {
          const isActive = activeStep === index;
          const isCompleted = index < activeStep;
          const isLast = index === steps.length - 1;
          const isFirst = index === 0;
          const stepApproved = isStepApproved(label);

          const approvalKey = nameMapWithNewFlow[label as keyof typeof nameMapWithNewFlow];

          return (
            <ArrowStep
              key={index}
              isActive={isActive}
              isCompleted={isCompleted}
              activeColor={activeColor}
              isLast={isLast}
              isFirst={isFirst}
              height={height}
              onClick={() => handleStepClick(index)}
            >
              {showStepIcon && (
                <>
                  {stepApproved ? (
                    <ApprovalIcon>
                      <CheckCircleIcon
                        sx={{
                          color: '#23A566',
                          fontSize: '20px',
                        }}
                      />
                    </ApprovalIcon>
                  ) : (
                    <StepCircle isActive={isActive}>{showNumber ? index + 1 : ''}</StepCircle>
                  )}
                </>
              )}
              <Tooltip title={label} arrow>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <StepLabel>{label}</StepLabel>
                  {approvalKey && SolutionIsThere && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          px: 1.5,
                          marginTop: 1,
                          py: 0.5,
                          borderRadius: '12px',
                          backgroundColor: getColorMap(
                            `${
                              generalInfo?.approval_state
                                ? generalInfo?.approval_state[
                                    approvalKey as keyof typeof generalInfo.approval_state
                                  ] == 'APPROVED'
                                  ? 'Approved'
                                  : 'Approval Pending'
                                : 'Approval Pending'
                            }`
                          ).bg,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            lineHeight: '100%',
                            color: getColorMap(
                              `${
                                generalInfo?.approval_state
                                  ? generalInfo?.approval_state[
                                      approvalKey as keyof typeof generalInfo.approval_state
                                    ] == 'APPROVED'
                                    ? 'Approved'
                                    : 'Approval Pending'
                                  : 'Approval Pending'
                              }`
                            ).color,
                          }}
                        >
                          {generalInfo?.approval_state
                            ? generalInfo?.approval_state[
                                approvalKey as keyof typeof generalInfo.approval_state
                              ] == 'APPROVED'
                              ? 'Approved'
                              : 'Approval Pending'
                            : 'Approval Pending'}
                        </Typography>
                      </Box>
                    )}
                </Box>
              </Tooltip>
            </ArrowStep>
          );
        })}
      </StepperContainer>
    </>
  );
};

export default NewArrowStepper;
